import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useCartaoHeader } from "@/lib/cartaoHeader";
import { AlertasRedeBell } from "@/components/AlertasRedeBell";
import { OnlineBadge } from "@/components/OnlineBadge";
import { usePresenca } from "@/lib/presenca";
import { useState, useEffect, Suspense } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import RevisaoNotificacoes from "@/components/RevisaoNotificacoes";
import MinhaConta from "@/components/MinhaConta";
import { CollapsedNav } from "@/components/CollapsedNav";
import { FeriadosAlerta } from "@/components/FeriadosAlerta";
import {
  LayoutDashboard, Moon, Shield, Building2, Users, Cpu, CalendarDays, ClipboardList,
  Clock, Calculator, FileSpreadsheet, FileText, LogOut, Fingerprint, ScrollText,
  ChevronRight, ChevronDown, Store, Activity, Briefcase, FolderOpen, Scale, ShieldAlert, Settings, Printer, Smartphone, Bell, UserCog, ScanFace, Lock, Menu, HardDriveUpload
} from "lucide-react";

const isDesktopNow = () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;

function useSidebar() {
  const [isDesktop, setIsDesktop] = useState(isDesktopNow);
  const [open, setOpen] = useState(() => {
    if (!isDesktopNow()) return false;
    try { return localStorage.getItem("cp_sidebar_open") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (!e.matches) setOpen(false);
      else { try { setOpen(localStorage.getItem("cp_sidebar_open") !== "0"); } catch { setOpen(true); } }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const toggle = () => setOpen((v) => {
    if (isDesktop) { try { localStorage.setItem("cp_sidebar_open", v ? "0" : "1"); } catch {} }
    return !v;
  });
  return { open, setOpen, isDesktop, toggle };
}

const grupoCadastros = {
  label: "Cadastros", icon: FolderOpen,
  itens: [
    { to: "/empresa", label: "Empresa", icon: Building2 },
    { to: "/horarios", label: "Horários", icon: Clock },
    { to: "/departamentos", label: "Departamentos", icon: ClipboardList },
    { to: "/funcoes", label: "Funções", icon: Briefcase },
    { to: "/funcionarios", label: "Funcionários", icon: Users },
    { to: "/equipamentos", label: "Equipamentos", icon: Cpu },
    // 🆕 Justificativas agora são GLOBAIS, gerenciadas apenas pelo Super Admin.
    // Oculto do menu do admin_empresa comum — super_admin ou impersonando continua vendo.
    { to: "/justificativas", label: "Justificativas", icon: FileText, requires_super: true },
    { to: "/feriados", label: "Feriados", icon: CalendarDays },
  ],
};

const grupoRelatorios = {
  label: "Relatórios", icon: FileSpreadsheet,
  itens: [
    { to: "/relatorios", label: "Ponto Diário", icon: FileSpreadsheet },
    { to: "/relatorios/adicional-noturno", label: "Adicional Noturno", icon: Moon },
    { to: "/relatorios/faltas", label: "Faltas", icon: FileText },
    { to: "/relatorios/horas-extras", label: "Horas Extras", icon: FileText },
    { to: "/relatorios/atestados", label: "Atestados", icon: FileText },
    { to: "/fiscal", label: "Arquivos Fiscais MTE", icon: ScrollText },
    // 🆕 Switches opt-in por empresa (Cadastros → Empresa → "Funcionalidades Extras").
    // Default OFF: só aparecem se o admin ativar `menu_banco_horas` em opcoes_avancadas.
    { to: "/banco-horas",  label: "Banco de Horas",     icon: Scale, requires_opt: "menu_banco_horas" },
    { to: "/painel-banco", label: "Painel Multi-Func",  icon: Users, requires_opt: "menu_banco_horas" },
  ],
};

const grupoConfiguracoes = {
  label: "Configurações", icon: Settings,
  itens: [
    { to: "/fila-sync", label: "Fila Sync Coletor", icon: Activity },
    // VPS Health, WebSocket Live e Diagnóstico Relógios: acesso pelo Painel SaaS (Ferramentas Técnicas).
    // Importar REP: removido do menu (rota /rep-importar continua ativa).
    // 🆕 11/02/2026 — Equipe (Co-Admins) movido pra dentro de Configurações.
    // grupoConfiguracoes já é filtrado p/ super_admin + impersonating,
    // garantindo que o cliente comum NÃO vê este item.
    { to: "/co-administradores", label: "Equipe de Administradores", icon: UserCog },
  ],
};

const menuAdmin = [
  { to: "/", label: "Início", icon: LayoutDashboard },
  grupoCadastros,
  { to: "/cartao-ponto", label: "Cartão de Ponto", icon: Fingerprint },
  // 🔕 Ocultos a pedido do usuário (produção):
  // { to: "/revisao-fraude", label: "Revisão de Fraude", icon: ScanFace },
  // { to: "/monitoramento", label: "Monitoramento", icon: Activity, requires_flag: "monitoramento_ativo" },
  { to: "/app-usuarios", label: "App Funcionários", icon: Smartphone },
  // 🆕 Switch opt-in CONTROLADO PELO SAAS (SuperAdmin → Editar Cliente → "App Facial Coletivo").
  //    Default OFF — só aparece se o Super Admin ativou a feature pro cliente.
  { to: "/app-facial-coletivo", label: "App Facial Coletivo", icon: ScanFace, requires_opt: "menu_facial_coletivo" },
  // 🆕 Switch opt-in por empresa — default OFF.
  { to: "/impressao-folha", label: "Impressão Folha", icon: Printer, requires_opt: "menu_impressao_folha" },
  grupoRelatorios,
  grupoConfiguracoes,
  { to: "/termos", label: "Termos & Compliance", icon: Shield },
];

const menuSuper = [
  { to: "/superadmin", label: "Painel Clientes", icon: Shield },
  { to: "/superadmin/monitoramento-global", label: "Monitoramento Global", icon: Activity },
  { to: "/superadmin/atualizacao-coletores", label: "Atualização de Coletores", icon: HardDriveUpload },
  { to: "/superadmin/recuperacao-geral", label: "Recuperação Geral", icon: Activity },
  { to: "/superadmin/comprovantes", label: "Comprovantes E-mail", icon: FileText },
  { to: "/superadmin/coleta-monitor", label: "Monitor de Coleta", icon: Activity },
  { to: "/justificativas", label: "Justificativas Globais", icon: FileText },
  { to: "/logins-suspeitos", label: "Logins Suspeitos", icon: ShieldAlert },
  { to: "/superadmin/seguranca", label: "Segurança (IP Block)", icon: Lock },
  { to: "/termos", label: "Termos & Compliance", icon: ScrollText },
];

function GroupNav({ group }) {
  const storageKey = `cp_nav_group_${group.label}`;
  const [open, setOpen] = useState(() => {
    // Carrega a última preferência do usuário (fechado por padrão)
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
  };
  const Icon = group.icon;
  return (
    <div className="mb-1">
      <button data-testid={`nav-group-${group.label.toLowerCase()}`} onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-900/5">
        <span className="flex items-center gap-3"><Icon size={16} /> {group.label}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="bg-slate-100/60">
          {group.itens.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.to === "/relatorios"}
              data-testid={`nav-${m.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 pl-10 pr-5 py-2 text-sm border-l-3 border-transparent ${isActive ? "active" : "text-slate-700 hover:text-slate-900"}`}>
              <m.icon size={14} /> {m.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, empresa, logout, impersonating, superAdminEmail, stopImpersonation } = useAuth();
  const noCartao = useLocation().pathname.startsWith("/cartao-ponto");
  const cartaoInfo = useCartaoHeader();
  usePresenca(!!user && user.role !== "super_admin" && !impersonating);
  const isSuper = user?.role === "super_admin";
  // Configurações só aparece para super_admin OU quando o super_admin está impersonando
  // um cliente (modo Suporte Técnico). Ficam ocultas para o admin_empresa comum,
  // mas as rotas continuam funcionais caso o super acesse pela URL.
  // 🆕 Dois tipos de flag:
  //   - requires_flag (top-level, DEFAULT=ON — p/ empresas antigas sem campo)
  //   - requires_opt  (opcoes_avancadas, DEFAULT=OFF — opt-in explícito pelo admin)
  const empresaFlags = empresa || {};
  const opcoes = empresaFlags.opcoes_avancadas || {};
  const checkFlag = (item) => {
    if (isSuper) return true;
    // 🆕 requires_super: só mostra pra super_admin ou super impersonando um cliente
    if (item.requires_super && !impersonating) return false;
    if (item.requires_flag && empresaFlags[item.requires_flag] === false) return false;
    if (item.requires_opt && opcoes[item.requires_opt] !== true) return false;
    return true;
  };
  // 🆕 Filtra também os itens DENTRO dos grupos (ex.: Banco de Horas em grupoRelatorios).
  const filterGroupItens = (m) => {
    if (!m || !Array.isArray(m.itens)) return m;
    const itens = m.itens.filter(checkFlag);
    return { ...m, itens };
  };
  const menuAdminFiltered = (impersonating
    ? menuAdmin
    : menuAdmin.filter((m) => m !== grupoConfiguracoes)
  ).filter(checkFlag).map(filterGroupItens).filter((m) => !m.itens || m.itens.length > 0);
  const somenteConsulta = !!user?.somente_consulta && !impersonating;
  useEffect(() => {
    document.body.classList.toggle("somente-consulta", somenteConsulta);
    return () => { document.body.classList.remove("somente-consulta"); };
  }, [somenteConsulta]);
  const PERMITIDOS_CONSULTA = ["/cartao-ponto", "/horarios", "/termos",
    "/relatorios", "/relatorios/adicional-noturno", "/fiscal", "/banco-horas", "/painel-banco"];
  const menuConsulta = menuAdmin.map(filterGroupItens)
    .map((m) => (m.itens ? { ...m, itens: m.itens.filter((i) => PERMITIDOS_CONSULTA.includes(i.to)) } : m))
    .filter((m) => (m.itens ? m.itens.length > 0 : PERMITIDOS_CONSULTA.includes(m.to)));
  const menu = isSuper ? menuSuper : somenteConsulta ? menuConsulta : menuAdminFiltered;

  // Modal "Minha Conta" visível só pro Super Admin (quando NÃO está impersonando).
  const [minhaContaOpen, setMinhaContaOpen] = useState(false);
  const side = useSidebar();
  const closeOnMobileNav = (e) => { if (!side.isDesktop && e.target.closest("a")) side.setOpen(false); };
  const collapsed = side.isDesktop && !side.open;
  const showMinhaConta = isSuper && !impersonating;

  // Badge de notificações (atestados + justificativas-ponto pendentes do PWA).
  // Visível apenas para admin_empresa (ou super impersonando). Polling 30s.
  const [notifTotal, setNotifTotal] = useState(0);
  const [notifColetas, setNotifColetas] = useState(0);
  const [notifPend, setNotifPend] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const showBell = !isSuper || impersonating;
  const refreshNotif = async () => {
    try {
      const r = await api.get("/notificacoes-admin");
      setNotifTotal(r.data?.total || 0);
      setNotifColetas(r.data?.coletas_concluidas || 0);
      setNotifPend((r.data?.atestados_pendentes || 0) + (r.data?.justificativas_pendentes || 0));
    } catch { /* silencia */ }
  };
  useEffect(() => {
    if (!showBell) return;
    refreshNotif();
    const id = setInterval(() => { if (!document.hidden) refreshNotif(); }, 30000);
    return () => clearInterval(id);
  }, [showBell]);
  return (
    <div className="min-h-screen bg-slate-50">
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white shadow-lg" data-testid="impersonation-banner">
          <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
            <ShieldAlert size={18} className="shrink-0" />
            <div className="text-sm flex-1 min-w-0">
              <strong>Modo Suporte Técnico ativo:</strong>{" "}
              Você (<span className="font-mono">{superAdminEmail}</span>) está acessando como{" "}
              <strong className="font-mono">{user?.email}</strong> ({empresa?.razao_social}).
              O cliente real continua logado em paralelo. <span className="opacity-80">Sessão expira em 2h.</span>
            </div>
            <button data-testid="btn-stop-impersonation" onClick={stopImpersonation}
              className="h-8 px-3 rounded-md bg-white text-amber-700 text-xs font-bold hover:bg-amber-50 shrink-0 inline-flex items-center gap-1.5">
              <LogOut size={12} /> Voltar ao Painel SaaS
            </button>
          </div>
        </div>
      )}
      {side.open && !side.isDesktop && (
        <div className="fixed inset-0 bg-slate-900/40 z-[25] lg:hidden" onClick={() => side.setOpen(false)} data-testid="sidebar-backdrop" />
      )}
      <aside className={`cp-sidebar ${collapsed ? "w-16" : "w-64"} fixed left-0 bottom-0 h-screen text-slate-700 flex flex-col z-30 transition-[width,transform] duration-300 ease-in-out ${side.open || side.isDesktop ? "translate-x-0" : "-translate-x-full"} ${impersonating ? "top-11" : "top-0"}`}
        data-testid="sidebar" data-state={side.open ? "open" : collapsed ? "collapsed" : "closed"}>
        {collapsed ? (
          <div className="py-4 border-b border-slate-200 grid place-items-center">
            <img src="/favicon-192.png" alt="DigiPonto" className="h-9 w-9 rounded-lg object-contain" data-testid="sidebar-logo-mini" />
          </div>
        ) : (
        <div className="px-4 py-4 border-b border-slate-200">
          <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-200 flex items-center justify-center">
            <img src="/digiponto-logo.jpeg" alt="DigiPonto" className="h-10 w-auto object-contain" />
          </div>
          <div className="text-[10px] text-slate-500 text-center mt-2 tracking-wide">Precisão que reconhece</div>
        </div>
        )}
        {collapsed ? <CollapsedNav menu={menu} /> : (
        <nav className="flex-1 overflow-y-auto py-2" onClick={closeOnMobileNav}>
          {menu.map((m, idx) => m.itens ? (
            <GroupNav key={idx} group={m} />
          ) : (
            <NavLink key={m.to} to={m.to} end={m.to === "/"}
              data-testid={`nav-${m.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm border-l-3 border-transparent ${isActive ? "active" : "text-slate-700 hover:text-slate-900 hover:bg-slate-900/5"}`}>
              <m.icon size={16} /> {m.label}
            </NavLink>
          ))}
        </nav>
        )}
        {collapsed ? (
          <div className="p-3 border-t border-slate-200 grid place-items-center">
            <button data-testid="btn-logout" onClick={logout} title="Sair" aria-label="Sair"
              className="h-10 w-10 grid place-items-center rounded-lg bg-slate-900 hover:bg-slate-800 text-white">
              <LogOut size={16} />
            </button>
          </div>
        ) : (
        <div className="p-4 border-t border-slate-200 bg-white/50">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {isSuper ? "Super Admin" : empresa?.razao_social}
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate">{user?.nome}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <button data-testid="btn-logout" onClick={logout}
            className="mt-3 w-full flex items-center justify-center gap-2 h-9 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold">
            <LogOut size={14} /> Sair
          </button>
        </div>
        )}
      </aside>
      <main className={`min-w-0 min-h-screen transition-[margin] duration-300 ease-in-out ${side.isDesktop ? (side.open ? "ml-64" : "ml-16") : "ml-0"} ${impersonating ? "pt-11" : ""}`}>
        <header className={`h-16 border-b border-slate-200 bg-white/90 backdrop-blur flex items-center px-3 sm:px-6 gap-2 justify-between sticky z-20 ${impersonating ? "top-11" : "top-0"}`} data-testid="top-header">
          <div className="flex items-center gap-3 text-sm text-slate-500 min-w-0">
            <button type="button" onClick={side.toggle} data-testid="btn-toggle-sidebar"
              title={side.open ? "Recolher menu" : "Expandir menu"} aria-label={side.open ? "Recolher menu" : "Expandir menu"}
              aria-expanded={side.open}
              className="h-9 w-9 shrink-0 grid place-items-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
              <Menu size={18} />
            </button>
            {noCartao && cartaoInfo?.empresa ? (
              <div className="min-w-0 leading-tight" data-testid="cartao-header-info">
                <div className="font-semibold text-slate-700 truncate" data-testid="cartao-header-empresa" title={cartaoInfo.empresa}>{cartaoInfo.empresa}</div>
                {cartaoInfo.funcionario && (
                  <div className="font-bold text-slate-900 text-sm sm:text-base sm:truncate" title={`${cartaoInfo.funcionario}${cartaoInfo.periodo ? ` | Período: ${cartaoInfo.periodo}` : ""}`}>
                    <span data-testid="cartao-header-funcionario" className="block truncate sm:inline">{cartaoInfo.funcionario}</span>
                    {cartaoInfo.periodo && (
                      <span className="block sm:inline font-semibold text-slate-600 text-[11px] sm:text-sm leading-tight whitespace-nowrap" data-testid="cartao-header-periodo"><span className="hidden sm:inline"> | </span>Período: {cartaoInfo.periodo}</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <span className="font-semibold text-slate-700 truncate flex items-center" data-testid="top-empresa">{isSuper ? "Painel SaaS" : empresa?.razao_social || empresa?.nome_fantasia}{isSuper && !impersonating && <OnlineBadge />}</span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="chip chip-emerald hidden lg:inline-flex">Sistema Online</div>
            {showMinhaConta && (
              <button
                data-testid="btn-minha-conta"
                onClick={() => setMinhaContaOpen(true)}
                title="Minha conta — perfil e segurança"
                className="h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-700 text-sm font-semibold inline-flex items-center gap-2"
              >
                <UserCog size={14} /> Minha Conta
              </button>
            )}
            {isSuper && !impersonating && <AlertasRedeBell />}
            {showBell && (
              <button
                data-testid="btn-notificacoes"
                onClick={() => setNotifOpen(true)}
                title={notifTotal > 0 ? `${notifTotal} notificação(ões)${notifColetas ? ` · ${notifColetas} coleta(s) concluída(s)` : ""}` : "Sem notificações"}
                className="relative h-9 w-9 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-100 text-slate-700"
              >
                <Bell size={16} />
                {notifTotal > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center animate-pulse"
                  >
                    {notifTotal > 99 ? "99+" : notifTotal}
                  </span>
                )}
              </button>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight min-w-0 max-w-[260px]">
              <span className="text-sm font-semibold text-slate-800 truncate" data-testid="top-user-nome">{user?.nome}</span>
              <span className="text-[11px] text-slate-500 truncate" data-testid="top-user-email">{user?.email}</span>
            </div>
            <button data-testid="top-btn-logout" onClick={logout}
              className="h-9 px-3 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center gap-2">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </header>
        <div className="p-6"><FeriadosAlerta ativo={!!empresa && user?.role !== "super_admin" || !!impersonating} empresaId={empresa?.id} /><Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-slate-400 text-sm" data-testid="page-loading">Carregando…</div>}><Outlet /></Suspense></div>
      </main>
      {notifOpen && (
        <RevisaoNotificacoes
          onClose={() => setNotifOpen(false)}
          onChange={refreshNotif}
          coletasNovas={notifColetas}
          abaInicial={notifColetas > 0 && notifPend === 0 ? "coletas" : "atestados"}
        />
      )}
      {minhaContaOpen && (
        <MinhaConta onClose={() => setMinhaContaOpen(false)} />
      )}
    </div>
  );
}
